import { type Pt, type Ring } from "./geometry"
import {
  type CutPin,
  type CutPlans,
  type PatternKind,
  type RoomProduct,
  type RoomSeams,
  type SeamOffset,
  type WasteMode,
  normalizePin,
} from "./document"

// Seam planning math — pure, dependency-free, vitest-covered. The Konva side
// renders what this module derives and never computes geometry itself.
//
// Model: a roll-goods room is covered by strips of roll material. All work
// happens in LAY-SPACE — the room rotated by -angle about its anchor
// (outline[0], the same corner patterns pivot on) and scaled to feet — where
// the roll's length runs along +x and strips stack along +y. Seam angle
// shares layout_angle's semantics, so directionLabel words apply to both.
//
// offsets are the strip BOUNDARIES (each one is a length seam), ascending,
// anchor-relative. The strips are the regions between the room's lay-space
// y-extent and those boundaries. cross seams (T-seams) cut a strip at a
// lay-space x. Cuts are DERIVED from this every time — never stored.
//
// The one rule everything here obeys (refinements 2026-08-06): caps bind the
// GENERATOR, never the human. proposeSeams honors usable width; dragSeam
// only enforces physics (a strip cannot be wider than the roll) and
// otherwise lets the estimator do anything, flagging instead of blocking.

/** Product types that get seam planning instead of a tile/plank pattern. */
export const ROLL_GOOD_TYPES = new Set(["Carpet", "Sheet Vinyl"])

export function rollGood(product: RoomProduct | null | undefined): boolean {
  return product?.product_type != null && ROLL_GOOD_TYPES.has(product.product_type)
}

/** The admin knobs the engine consumes, lengths converted to feet. The
 *  waste-suggestion table rides along so one settings object serves both the
 *  seam engine and the pattern-pick waste default. */
export type SeamSettings = {
  cutMarginFt: number
  cutGapFt: number
  usableWidthTrimFt: number
  minFillFt: number
  remnantMinLenFt: number
  rollLengthFt: number
  wasteSuggestions: Record<PatternKind, number>
}

type AdminKnobs = {
  cut_margin_in: number
  cut_gap_in: number
  usable_width_trim_in: number
  min_fill_in: number
  remnant_min_length_ft: number
  roll_length_ft: number
  waste_suggestions: Record<PatternKind, number>
}

export function toSeamSettings(admin: AdminKnobs): SeamSettings {
  return {
    cutMarginFt: admin.cut_margin_in / 12,
    cutGapFt: admin.cut_gap_in / 12,
    usableWidthTrimFt: admin.usable_width_trim_in / 12,
    minFillFt: admin.min_fill_in / 12,
    remnantMinLenFt: admin.remnant_min_length_ft,
    rollLengthFt: admin.roll_length_ft,
    wasteSuggestions: admin.waste_suggestions,
  }
}

/** The migration's defaults — the sample playground has no server props. */
export const FALLBACK_SETTINGS: SeamSettings = toSeamSettings({
  cut_margin_in: 3,
  cut_gap_in: 4,
  usable_width_trim_in: 2,
  min_fill_in: 6,
  remnant_min_length_ft: 5,
  roll_length_ft: 120,
  waste_suggestions: { straight: 10, brick: 10, third: 10, herringbone: 15, stagger: 10 },
})

/**
 * The width all cut math runs against: the estimator's explicit usable width,
 * else nominal minus the admin trim. Nominal keeps printing on documents —
 * "nominal for print, usable for math".
 */
export function usableWidthFt(
  product: RoomProduct | null | undefined,
  settings: SeamSettings
): number | null {
  if (!product) return null
  if (product.usable_width_ft != null && product.usable_width_ft > 0) return product.usable_width_ft
  if (product.roll_width_ft != null && product.roll_width_ft > 0) {
    return Math.max(product.roll_width_ft - settings.usableWidthTrimFt, 0.5)
  }
  return null
}

// ---------------------------------------------------------------------------
// Lay-space transform

const rad = (deg: number) => (deg * Math.PI) / 180

/** World px -> lay-space ft, anchored at the room's first outline vertex. */
export function toLay(pt: Pt, anchor: Pt, angle: number, ftPerPx: number): Pt {
  const a = rad(angle)
  const dx = pt[0] - anchor[0]
  const dy = pt[1] - anchor[1]
  return [(Math.cos(a) * dx + Math.sin(a) * dy) * ftPerPx, (-Math.sin(a) * dx + Math.cos(a) * dy) * ftPerPx]
}

/** Lay-space ft -> world px. Inverse of toLay, for rendering seam lines. */
export function toWorld(pt: Pt, anchor: Pt, angle: number, ftPerPx: number): Pt {
  const a = rad(angle)
  const x = pt[0] / ftPerPx
  const y = pt[1] / ftPerPx
  return [anchor[0] + Math.cos(a) * x - Math.sin(a) * y, anchor[1] + Math.sin(a) * x + Math.cos(a) * y]
}

export function layRing(ring: Ring, anchor: Pt, angle: number, ftPerPx: number): Ring {
  return ring.map((pt) => toLay(pt, anchor, angle, ftPerPx))
}

export type LaySpan = { minX: number; maxX: number; minY: number; maxY: number }

export function laySpan(ring: Ring): LaySpan {
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const [x, y] of ring) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { minX, maxX, minY, maxY }
}

// ---------------------------------------------------------------------------
// Strips

/** Seams can compress a strip this narrow, never to zero (degenerate). */
export const MIN_STRIP_FT = 0.1

export type Strip = {
  index: number
  y0: number
  y1: number
  width: number
}

/** The strip regions a boundary list induces over the room's y-extent. */
export function strips(span: LaySpan, offsets: SeamOffset[]): Strip[] {
  const sorted = [...offsets].map((o) => o.pos_ft).sort((a, b) => a - b)
  const edges = [span.minY, ...sorted, span.maxY]
  const out: Strip[] = []
  for (let i = 0; i < edges.length - 1; i++) {
    out.push({ index: i, y0: edges[i], y1: edges[i + 1], width: edges[i + 1] - edges[i] })
  }
  return out
}

// ---------------------------------------------------------------------------
// Propose / drag / edit

/**
 * The initial layout: full usable-width strips racked from the side opposite
 * the fill, remainder as the fill strip. No optimization — that is v2's
 * "suggest layout" button; this is the starting point the estimator edits.
 */
export function proposeSeams(
  outlineLay: Ring,
  angle: number,
  usableW: number,
  fillSide: "start" | "end" = "end"
): RoomSeams {
  const span = laySpan(outlineLay)
  const total = span.maxY - span.minY
  const positions: number[] = []
  if (total > usableW) {
    const fullStrips = Math.ceil(total / usableW) - 1
    for (let i = 1; i <= fullStrips; i++) {
      positions.push(fillSide === "end" ? span.minY + usableW * i : span.maxY - usableW * i)
    }
    positions.sort((a, b) => a - b)
  }
  return {
    angle,
    offsets: positions.map((pos_ft) => ({ pos_ft, locked: false })),
    cross: [],
    fill_side: fillSide,
    locked: false,
    auto: true,
  }
}

/**
 * RT's drag mechanic (2026-08-07): full strips STAY full — dragging a seam
 * pushes the unlocked seams ahead of it along rigidly (each keeps its strip
 * width) and the strip against the far wall absorbs the movement, down to
 * MIN_STRIP_FT. A LOCKED seam is a wall: the push stops there, so dragging
 * toward one deliberately compresses the strip between into a narrow fill
 * strip. On the growth side a strip can never exceed the roll's usable
 * width — the material isn't that wide — so once the strip behind is full,
 * further travel drags that seam along too, cascading until everything
 * behind is at full width and the drag clamps. Narrow strips below the
 * min-fill knob are flagged elsewhere, never blocked.
 *
 * Returns the new offsets, sorted, lock flags travelling with their seams.
 */
export function dragSeam(
  offsets: SeamOffset[],
  index: number,
  target: number,
  usableW: number,
  span: LaySpan
): SeamOffset[] {
  const n = offsets.length
  const pos = offsets.map((o) => o.pos_ft)
  const lockedFlags = offsets.map((o) => o.locked)
  const requested = target - pos[index]
  if (requested === 0) return [...offsets]
  const dir = requested > 0 ? 1 : -1

  // Ahead: the rigid chain runs to the first locked seam (or the room edge,
  // which is as immovable); the strip adjacent to that wall absorbs the
  // travel, down to MIN_STRIP_FT.
  const chain: number[] = []
  let wall = dir > 0 ? span.maxY : span.minY
  for (let j = index + dir; j >= 0 && j < n; j += dir) {
    if (lockedFlags[j]) {
      wall = pos[j]
      break
    }
    chain.push(j)
  }
  const chainEnd = chain.length ? pos[chain[chain.length - 1]] : pos[index]
  const absorb = Math.abs(wall - chainEnd)
  const travelAhead = Math.max(absorb - MIN_STRIP_FT, 0)

  // Behind: each strip back to the first locked seam (or edge) can grow to
  // usableW; the total headroom bounds the drag (physics, not policy).
  let budgetBehind = 0
  let prev = pos[index]
  for (let j = index - dir; ; j -= dir) {
    const boundary = j >= 0 && j < n ? pos[j] : (dir > 0 ? span.minY : span.maxY)
    budgetBehind += Math.max(usableW - Math.abs(prev - boundary), 0)
    if (j >= 0 && j < n && lockedFlags[j]) break
    if (j < 0 || j >= n) break
    prev = boundary
  }

  const travel = Math.min(Math.abs(requested), travelAhead, budgetBehind)
  if (travel <= 0) return [...offsets]

  pos[index] += dir * travel
  // The chain ahead moves rigidly — every full strip stays full.
  for (const j of chain) pos[j] += dir * travel
  // Behind, growth cascades: each strip fills to usableW before the seam
  // behind it starts to move.
  let overflow = travel
  prev = pos[index] - dir * travel // original position of the dragged seam
  for (let j = index - dir; j >= 0 && j < n; j -= dir) {
    const width = Math.abs(prev - pos[j])
    overflow = Math.max(overflow - (usableW - width), 0)
    if (overflow <= 0) break
    prev = pos[j]
    pos[j] += dir * overflow
    if (lockedFlags[j]) break // clamped by budgetBehind already; belt & braces
  }

  return pos
    .map((pos_ft, j) => ({ pos_ft, locked: lockedFlags[j] }))
    .sort((a, b) => a.pos_ft - b.pos_ft)
}

// ---------------------------------------------------------------------------
// Cuts

export type Cut = {
  /** "<room client_id>:<copy>:<strip>:<segment>" — the persistent identity. */
  key: string
  roomId: string
  roomName: string
  copy: number
  strip: number
  segment: number
  /** Strip width — what the piece measures across the roll. */
  widthFt: number
  /** Along the roll: x-extent + cut margin. Repeat allowance is added at
   *  assignment time (it depends on roll position). */
  lengthFt: number
  /** Below the min-fill knob — flagged, never blocked. */
  narrowFill: boolean
  /** The piece's true outline in the cut's own frame (feet; origin at the
   *  segment's x start / the strip's y0). The widthFt × lengthFt rectangle
   *  is the MATERIAL cut off the roll; this polygon is the part the floor
   *  actually uses — an L-room's corner strip renders as its real L, a
   *  U-room's bridged strip shows its gap as waste. Deduction holes stay
   *  out by design (they're waste, never shorter cuts). Derived, never
   *  stored; fewer than 3 points means the segment covers no floor. */
  shape: Ring
}

/**
 * Clip a ring to the horizontal band [y0, y1] (Sutherland–Hodgman against
 * two half-planes; a band is convex so the result is a single ring). Only
 * the x-extent of the result matters to cut math.
 */
export function clipToBand(ring: Ring, y0: number, y1: number): Ring {
  const clipHalf = (input: Ring, inside: (p: Pt) => boolean, cross: (a: Pt, b: Pt) => Pt): Ring => {
    const out: Ring = []
    for (let i = 0; i < input.length; i++) {
      const a = input[i]
      const b = input[(i + 1) % input.length]
      const aIn = inside(a)
      const bIn = inside(b)
      if (aIn) out.push(a)
      if (aIn !== bIn) out.push(cross(a, b))
    }
    return out
  }
  const atY = (a: Pt, b: Pt, y: number): Pt => {
    const t = (y - a[1]) / (b[1] - a[1])
    return [a[0] + (b[0] - a[0]) * t, y]
  }
  let out = clipHalf(ring, (p) => p[1] >= y0, (a, b) => atY(a, b, y0))
  if (out.length) out = clipHalf(out, (p) => p[1] <= y1, (a, b) => atY(a, b, y1))
  return out
}

const swapXY = (ring: Ring): Ring => ring.map(([x, y]): Pt => [y, x])

/** clipToBand rotated 90° — clips a ring to the vertical band [x0, x1]. */
export function clipToXBand(ring: Ring, x0: number, x1: number): Ring {
  return swapXY(clipToBand(swapXY(ring), x0, x1))
}

/**
 * Where a seam line actually crosses the room: the scanline intersection of
 * an axis-aligned line with the polygon, as sorted [start, end] intervals.
 * A length seam (axis "y", horizontal in lay-space) yields x-intervals; a
 * T-seam (axis "x", vertical) yields y-intervals. An L-shaped room clips
 * the seam to the floor; a U-shaped room yields multiple segments — this is
 * what the renderer draws instead of the bounding-box span.
 */
export function seamSpans(ring: Ring, axis: "x" | "y", pos: number): [number, number][] {
  const crossings: number[] = []
  const coord = axis === "y" ? 1 : 0 // which component the line fixes
  const other = axis === "y" ? 0 : 1
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]
    const b = ring[(i + 1) % ring.length]
    const a1 = a[coord]
    const b1 = b[coord]
    // Half-open interval per edge so a vertex crossing counts once.
    if (a1 <= pos === b1 <= pos) continue
    const t = (pos - a1) / (b1 - a1)
    crossings.push(a[other] + (b[other] - a[other]) * t)
  }
  crossings.sort((p, q) => p - q)
  const spans: [number, number][] = []
  for (let i = 0; i + 1 < crossings.length; i += 2) {
    if (crossings[i + 1] - crossings[i] > 1e-9) spans.push([crossings[i], crossings[i + 1]])
  }
  return spans
}

/** What deriveCuts needs to know about a room — a structural EditorRoom. */
export type SeamRoom = {
  id: string
  name: string
  outline: Ring
  holes: Ring[]
  multiplier: number
  seams: RoomSeams
}

/**
 * The cut list a room's seam layout produces, multiplied rooms included
 * (copy k repeats every cut with its own key, so the copies pool onto shared
 * rolls downstream — that pooling is the point of the multiplier).
 *
 * A cut spans the full x-extent of its strip ∩ room — a U-shaped room's gap
 * is carried as waste unless the estimator splits it with a T-seam, which is
 * exactly the manual-control contract.
 *
 * Cuts are rectangles of material: deduction holes make waste, not shorter
 * cuts, so holes never enter the clip — they show up in computed waste as
 * the gap between cut area and net area.
 */
export function deriveCuts(room: SeamRoom, ftPerPx: number, settings: SeamSettings): Cut[] {
  const { seams } = room
  const anchor = room.outline[0]
  const outlineLay = layRing(room.outline, anchor, seams.angle, ftPerPx)
  const span = laySpan(outlineLay)
  const stripList = strips(span, seams.offsets)

  const base: Omit<Cut, "key" | "copy">[] = []
  for (const strip of stripList) {
    const clipped = clipToBand(outlineLay, strip.y0, strip.y1)
    if (clipped.length < 3) continue
    const extent = laySpan(clipped)

    // T-seams inside this strip partition its x-extent into segments.
    const cuts = seams.cross
      .filter((c) => c.strip === strip.index && c.pos_ft > extent.minX && c.pos_ft < extent.maxX)
      .map((c) => c.pos_ft)
      .sort((a, b) => a - b)
    const edges = [extent.minX, ...cuts, extent.maxX]

    for (let s = 0; s < edges.length - 1; s++) {
      const rawLen = edges[s + 1] - edges[s]
      if (rawLen <= 0) continue
      const piece = edges.length > 2 ? clipToXBand(clipped, edges[s], edges[s + 1]) : clipped
      base.push({
        roomId: room.id,
        roomName: room.name,
        strip: strip.index,
        segment: s,
        widthFt: strip.width,
        lengthFt: rawLen + settings.cutMarginFt,
        narrowFill: strip.width < settings.minFillFt,
        shape: piece.map(([x, y]): Pt => [x - edges[s], y - strip.y0]),
      })
    }
  }

  const out: Cut[] = []
  for (let copy = 0; copy < Math.max(room.multiplier, 1); copy++) {
    for (const cut of base) {
      out.push({ ...cut, copy, key: `${room.id}:${copy}:${cut.strip}:${cut.segment}` })
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Roll assignment

export type RollCut = Cut & {
  /** 1-based cut number, unique across the product's rolls — what the
   *  warehouse reads off the printed sheet. */
  number: number
  /** Position on the roll: x along the length, y across the width (feet). */
  xFt: number
  yFt: number
  /** Dead pattern-repeat span paid IN FRONT of this cut's group. */
  repeatFt: number
  pinned: boolean
  /** Hangs past the roll's end — rendered red, never refused. */
  overrun: boolean
  /** Intersects another cut (only hand-placed pile-ups can) — same deal. */
  overlap: boolean
}

export type Roll = {
  index: number
  cuts: RollCut[]
  /** Occupied extent along the roll — how much material the roll consumes. */
  usedFt: number
  leftoverFt: number
  /** null while the roll is full to the brim (or overflowing). */
  leftoverKind: "remnant" | "waste" | null
  /** Something hangs past the end — flagged, kept (gate is the bid). */
  overflow: boolean
}

export type ProductAssignment = {
  rolls: Roll[]
  /** Total linear feet of roll consumed, repeats included — the order LF. */
  totalLf: number
  cutCount: number
  narrowFillCount: number
}

/**
 * Pattern-repeat allowance for a cut GROUP (cuts sharing a start position):
 * every group after a roll's first pays one repeat of dead material in
 * front, to line the pattern up with the piece before it; half-drop pays
 * half a repeat more (×1.5). Lane-mates ripped from the same span share the
 * group's allowance. Plain goods pay nothing.
 */
export function repeatAllowanceFt(product: RoomProduct, firstOnRoll: boolean): number {
  const repeat = product.repeat_len_in
  if (firstOnRoll || repeat == null || repeat <= 0) return 0
  return (repeat / 12) * (product.half_drop ? 1.5 : 1)
}

type Rect = { x0: number; x1: number; y0: number; y1: number; cut: RollCut }

/**
 * Do two placed pieces truly collide? Judged on the TRUE shapes, never the
 * material rectangles — a fill dropped into the empty half of an L-cut's
 * rectangle is legal nesting (RT, 2026-08-07); the red flag means floor
 * material would be cut twice. Detection: any proper edge crossing, or any
 * nudged vertex of one inside the other (even-odd, so the degenerate bridge
 * rings Sutherland–Hodgman emits for U-strips read their gap as outside).
 * The inward nudge catches exact-stacked pins; edges that merely touch —
 * lane-mates abutting, shared cross-cuts — never count. A piece straddling
 * a U-ring's bridge line can flag conservatively; acceptable, the flag
 * informs and never blocks.
 */
export function shapesOverlap(a: Ring, b: Ring): boolean {
  const sa = laySpan(a)
  const sb = laySpan(b)
  if (sa.minX >= sb.maxX - 1e-9 || sb.minX >= sa.maxX - 1e-9) return false
  if (sa.minY >= sb.maxY - 1e-9 || sb.minY >= sa.maxY - 1e-9) return false

  const orient = (p: Pt, q: Pt, r: Pt) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0])
  for (let i = 0; i < a.length; i++) {
    const p1 = a[i]
    const p2 = a[(i + 1) % a.length]
    for (let j = 0; j < b.length; j++) {
      const q1 = b[j]
      const q2 = b[(j + 1) % b.length]
      if (
        orient(p1, p2, q1) * orient(p1, p2, q2) < -1e-9 &&
        orient(q1, q2, p1) * orient(q1, q2, p2) < -1e-9
      ) {
        return true
      }
    }
  }

  const pointIn = (ring: Ring, [px, py]: Pt): boolean => {
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i]
      const [xj, yj] = ring[j]
      if (yi > py !== yj > py && xi + ((py - yi) / (yj - yi)) * (xj - xi) > px) inside = !inside
    }
    return inside
  }
  const probes = (ring: Ring, span: LaySpan): Ring => {
    const cx = (span.minX + span.maxX) / 2
    const cy = (span.minY + span.maxY) / 2
    const d = 1e-3
    return ring.map(([x, y]): Pt => [x + Math.sign(cx - x) * d, y + Math.sign(cy - y) * d])
  }
  return probes(a, sa).some((p) => pointIn(b, p)) || probes(b, sb).some((p) => pointIn(a, p))
}

/** A cut's true piece translated to a place on the roll — falling back to
 *  the material rect when the shape is degenerate (covers no floor). */
export function placedPiece(
  cut: Pick<Cut, "shape" | "lengthFt" | "widthFt">,
  xFt: number,
  yFt: number
): Ring {
  return cut.shape.length >= 3
    ? cut.shape.map(([px, py]): Pt => [px + xFt, py + yFt])
    : [
        [xFt, yFt],
        [xFt + cut.lengthFt, yFt],
        [xFt + cut.lengthFt, yFt + cut.widthFt],
        [xFt, yFt + cut.widthFt],
      ]
}

/**
 * Would dropping `cut` at (xFt, yFt) land on material a PINNED cut already
 * claims? The tray refuses such a drop outright (RT, 2026-08-07 — amends
 * flag-don't-block for cut placement). Only pins block: auto cuts re-flow
 * around the new pin, so no collision can result from landing on one —
 * that's the "bump out of the way" behavior. Empty regions of a pinned
 * cut's rectangle (an L's notch) stay claimable.
 */
export function dropCollides(
  cut: Pick<Cut, "key" | "shape" | "lengthFt" | "widthFt">,
  xFt: number,
  yFt: number,
  placed: Pick<RollCut, "key" | "pinned" | "shape" | "lengthFt" | "widthFt" | "xFt" | "yFt">[]
): boolean {
  const piece = placedPiece(cut, xFt, yFt)
  return placed.some(
    (c) => c.key !== cut.key && c.pinned && shapesOverlap(piece, placedPiece(c, c.xFt, c.yFt))
  )
}

/**
 * Free width intervals of [0, width] not covered by rects overlapping
 * (x0, x1). Each blocking rect is inflated by the cut gap on both sides, so
 * nested lane-mates keep clearance for the rip cut between them — a
 * drifting rip can't ruin its neighbour. The roll's own edges need no gap
 * (they're factory edges, not cuts), and pinned cuts sit exactly where the
 * human put them: the gap binds the generator only.
 */
function freeLanes(rects: Rect[], x0: number, x1: number, width: number, gap: number): [number, number][] {
  const blocking = rects
    .filter((r) => r.x0 < x1 - 1e-9 && r.x1 > x0 + 1e-9)
    .map((r): [number, number] => [r.y0 - gap, r.y1 + gap])
    .sort((a, b) => a[0] - b[0])
  const lanes: [number, number][] = []
  let cursor = 0
  for (const [b0, b1] of blocking) {
    if (b0 > cursor + 1e-9) lanes.push([cursor, b0])
    cursor = Math.max(cursor, b1)
  }
  if (cursor < width - 1e-9) lanes.push([cursor, width])
  return lanes
}

/**
 * Nest cuts onto numbered rolls in 2D — the teardown's "cut group"
 * compression. Narrow cuts pack side-by-side across the roll's usable width
 * (three 3'×10' fills consume ~10' of roll, not 30'), placed leftmost-first
 * at candidate positions, longest cuts first so lane-mates never outgrow
 * their group.
 *
 * Pinned cuts sit exactly where the human put them and everything else
 * flows around them (the re-solve contract). Hand placements may overlap
 * each other or hang past the roll's end — flagged `overlap`/`overrun`,
 * rendered red, never refused: invalid states are allowed and the bid is
 * the gate (teardown 2.3).
 */
export function assignCuts(
  cuts: Cut[],
  plan: { pins: Record<string, CutPin | number>; locks?: string[] } | undefined,
  product: RoomProduct,
  settings: SeamSettings
): ProductAssignment {
  const pins = new Map<string, CutPin>(
    Object.entries(plan?.pins ?? {}).map(([key, pin]) => [key, normalizePin(pin)])
  )
  const rollLen = settings.rollLengthFt
  const usableW =
    usableWidthFt(product, settings) ?? Math.max(...cuts.map((c) => c.widthFt), 1)

  const rollRects: Rect[][] = []
  const rollAt = (index: number) => {
    while (rollRects.length <= index) rollRects.push([])
    return rollRects[index]
  }

  const makeRollCut = (cut: Cut, pinned: boolean): RollCut => ({
    ...cut,
    number: 0,
    xFt: 0,
    yFt: 0,
    repeatFt: 0,
    pinned,
    overrun: false,
    overlap: false,
  })

  const placeAt = (rects: Rect[], rc: RollCut, x: number, y: number, repeatFt: number) => {
    rc.xFt = x
    rc.yFt = y
    rc.repeatFt = repeatFt
    rects.push({ x0: x - repeatFt, x1: x + rc.lengthFt, y0: y, y1: y + rc.widthFt, cut: rc })
  }

  // Hand placements first — they are the obstacles everything flows around.
  const positioned = cuts.filter((c) => {
    const pin = pins.get(c.key)
    return pin != null && pin.x_ft != null
  })
  for (const cut of positioned) {
    const pin = pins.get(cut.key)!
    placeAt(rollAt(pin.roll), makeRollCut(cut, true), pin.x_ft!, pin.y_ft ?? 0, 0)
  }

  // Auto placement: longest first (lane-mates never outgrow their group),
  // leftmost candidate position that has a free lane wide enough. Candidate
  // xs are 0 and every existing rect edge — starting AT an existing rect's
  // x0 is what shares its group (and its repeat allowance).
  const autoPlace = (cut: Cut, rollIndexes: number[] | null): void => {
    const rc = makeRollCut(cut, rollIndexes != null && rollIndexes.length === 1)
    for (let i = 0; ; i++) {
      if (rollIndexes && i >= rollIndexes.length) rollIndexes = null // constrained roll full → spill anywhere
      const rollIndex = rollIndexes ? rollIndexes[i] : i
      const rects = rollAt(rollIndex)
      const firstOnRoll = rects.length === 0
      const repeat = repeatAllowanceFt(product, firstOnRoll)
      const candidates = [
        ...new Set([0, ...rects.flatMap((r) => [r.x0, r.x1])]),
      ]
        .filter((x) => x >= 0)
        .sort((a, b) => a - b)
      for (const x of candidates) {
        // Sharing an existing group start (some rect at exactly x0 = x)
        // shares its repeat span; a fresh position pays its own.
        const shared = rects.some((r) => Math.abs(r.x0 + r.cut.repeatFt - x) < 1e-9)
        const repeatHere = shared ? 0 : repeat
        const x0 = x + repeatHere
        const x1 = x0 + cut.lengthFt
        if (x1 > rollLen + 1e-9) continue
        const lane = freeLanes(rects, x, x1, usableW, settings.cutGapFt).find(
          ([y0, y1]) => y1 - y0 >= cut.widthFt - 1e-9
        )
        if (lane) {
          placeAt(rects, rc, x0, lane[0], repeatHere)
          return
        }
      }
      // Nothing fits on this roll. A cut longer than the roll itself gets
      // its own roll, overrunning — flagged, never dropped.
      if (rects.length === 0) {
        placeAt(rects, rc, 0, 0, 0)
        return
      }
    }
  }

  const sorted = (list: Cut[]) =>
    [...list].sort((a, b) => b.lengthFt - a.lengthFt || cuts.indexOf(a) - cuts.indexOf(b))

  // Legacy roll-only pins ({roll} with no position) keep their roll but let
  // the packer position them; free cuts go anywhere.
  const rollConstrained = cuts.filter((c) => {
    const pin = pins.get(c.key)
    return pin != null && pin.x_ft == null
  })
  const free = cuts.filter((c) => !pins.has(c.key))
  for (const cut of sorted(rollConstrained)) autoPlace(cut, [pins.get(cut.key)!.roll])
  for (const cut of sorted(free)) autoPlace(cut, null)

  // Flags, numbering (reading order: roll, then x, then y), and totals.
  // Overlap is judged on the true piece shapes (shapesOverlap), so a pin
  // nested into the empty part of another cut's rectangle stays legal.
  let number = 0
  const outRolls: Roll[] = rollRects.map((rects, index) => {
    const pieces = new Map(rects.map((r) => [r, placedPiece(r.cut, r.cut.xFt, r.cut.yFt)]))
    for (const r of rects) {
      r.cut.overrun = r.x1 > rollLen + 1e-9
      r.cut.overlap = rects.some((o) => o !== r && shapesOverlap(pieces.get(r)!, pieces.get(o)!))
    }
    const cutList = rects
      .map((r) => r.cut)
      .sort((a, b) => a.xFt - b.xFt || a.yFt - b.yFt)
    for (const cut of cutList) cut.number = ++number
    const usedFt = rects.reduce((max, r) => Math.max(max, r.x1), 0)
    const leftoverFt = Math.max(rollLen - usedFt, 0)
    return {
      index,
      cuts: cutList,
      usedFt,
      leftoverFt,
      leftoverKind:
        leftoverFt < 1e-9 ? null : leftoverFt >= settings.remnantMinLenFt ? "remnant" : "waste",
      overflow: usedFt > rollLen + 1e-9,
    }
  })

  return {
    rolls: outRolls,
    totalLf: outRolls.reduce((sum, roll) => sum + roll.usedFt, 0),
    cutCount: cuts.length,
    narrowFillCount: cuts.filter((c) => c.narrowFill).length,
  }
}

/**
 * The re-solve contract's bookkeeping: pins and locks whose cut no longer
 * exists (room deleted, product changed, seams restructured) drop out;
 * everything else survives untouched. The store also clears a room's own
 * pins when its seam geometry changes — a lock whose premise changed is
 * meaningless (refinements, verbatim from the teardown).
 */
export function reconcileCutPlan(
  plans: CutPlans,
  validKeysByProduct: Record<string, Set<string>>
): CutPlans {
  const out: CutPlans = {}
  for (const [productId, plan] of Object.entries(plans)) {
    const valid = validKeysByProduct[productId]
    if (!valid) continue
    const pins = Object.fromEntries(Object.entries(plan.pins).filter(([key]) => valid.has(key)))
    const locks = plan.locks.filter((key) => valid.has(key))
    if (Object.keys(pins).length || locks.length) out[productId] = { pins, locks }
  }
  return out
}

// ---------------------------------------------------------------------------
// Metrics & order quantities

export type SeamMetrics = {
  lf: number
  cutCount: number
  /** LF × NOMINAL width — what actually gets bought (side trim is paid for). */
  purchasedSf: number | null
  /** purchasedSf − net: the waste the layout itself produces. */
  computedWasteSf: number | null
  /** Both spellings of the same number: ours ("+% of net") and the
   *  gross-fraction an ex-Measure estimator expects to see. */
  wastePctOfNet: number | null
  wastePctOfGross: number | null
}

export function seamMetrics(lf: number, cutCount: number, product: RoomProduct, netSf: number): SeamMetrics {
  const nominal = product.roll_width_ft
  if (nominal == null || nominal <= 0 || netSf <= 0) {
    return { lf, cutCount, purchasedSf: null, computedWasteSf: null, wastePctOfNet: null, wastePctOfGross: null }
  }
  const purchasedSf = lf * nominal
  const computedWasteSf = purchasedSf - netSf
  return {
    lf,
    cutCount,
    purchasedSf,
    computedWasteSf,
    wastePctOfNet: (computedWasteSf / netSf) * 100,
    wastePctOfGross: purchasedSf > 0 ? (computedWasteSf / purchasedSf) * 100 : null,
  }
}

/**
 * The order quantity under the waste-mode split (refinements 2026-08-06):
 * `net` ignores computed waste (rule of thumb, no cut plan needed);
 * `computed` starts from net + the layout's real waste. The user margin is a
 * markup on that base either way — "+% of net", the trade convention.
 * Multiplier scales net; computedWasteSf already covers all copies.
 */
export function orderSf(
  netSf: number | null,
  mode: WasteMode,
  computedWasteSf: number | null,
  wastePct: number,
  multiplier: number
): number | null {
  if (netSf == null) return null
  const baseNet = netSf * Math.max(multiplier, 1)
  const base = mode === "computed" && computedWasteSf != null ? baseNet + computedWasteSf : baseNet
  return base * (1 + wastePct / 100)
}

/** Waste % restated as Measure's gross fraction — the live hint beside the
 *  "+% of net" field, so a number typed from their world is caught on sight. */
export function grossEquivalentPct(wastePctOfNet: number): number {
  return (wastePctOfNet / (100 + wastePctOfNet)) * 100
}
